import {
  IonContent,
  IonPage,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import AppPageHeader from '../components/AppPageHeader';
import { useTranslation } from 'react-i18next';
import { apiAddPhysicalFarecard, apiGetFareCards } from '../services/api';
import AddPhysicalFarecardForm from '../usecases/farecard/AddPhysicalFarecardForm';
import {
  clearNumber,
  strHasMaxLength,
  strHasMinLength,
} from '../utils/strings';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import { useHistory } from 'react-router';
import { storageSetCards } from '../utils/storage';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const CardsAddPhysicalPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const router = useHistory();
  const [t] = useTranslation('global');
  const [present] = useToaster();

  const realCardLength = 16;
  const generatedCardLength = 19;
  const testCardLength = 20;

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [physicalNumber, setPhysicalNumber] = useState<string>('');
  const [physicalCode, setPhysicalCode] = useState<string>('');
  const [physicalName, setPhysicalName] = useState<string>('');

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setPhysicalCode('');
    setPhysicalName('');
    setPhysicalNumber('');
    setIsPageActive(false);
  });

  const getPhysicalValidationError = () => {
    const cardLength = clearNumber(physicalNumber)?.length ?? 0;

    if (
      cardLength !== realCardLength &&
      cardLength !== testCardLength &&
      cardLength !== generatedCardLength
    ) {
      return t('farecards.errorphysicalnumber', { v: realCardLength });
    }
    if (!strHasMinLength(physicalCode, 3)) {
      return t('farecards.errorphysicalcode');
    }
    if (!strHasMaxLength(physicalCode, 3)) {
      return t('farecards.errorphysicalcode');
    }
    if (!strHasMaxLength(physicalCode, 40)) {
      return t('farecards.errorname');
    }
    return false;
  };

  const addPhysicalFinish = async () => {
    if (isSubmitting) {
      return;
    }
    const validationError = getPhysicalValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiAddPhysicalFarecard(
        {
          cardId: clearNumber(physicalNumber),
          securityCode: physicalCode,
          nickname: physicalName,
        },
        auth,
      );
      await apiGetFareCards(auth).then((response) => {
        storageSetCards(response.data);
      });
      present(getAppSuccess(t('farecards.physicalsuccess')));
      router.push('/cards');
    } catch (error) {
      console.error('Error adding card ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('farecards.physicalnosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  return (
    <IonPage id="farecard-page">
      <>
        <AppPageHeader
          isPageActive={isPageActive}
          back={() => router.push('/cards')}
          title={t('farecards.createphysical')}
        ></AppPageHeader>
        <IonContent fullscreen className="ion-padding default-content">
          <AddPhysicalFarecardForm
            isValid={getPhysicalValidationError() === false}
            cardLength={realCardLength}
            number={physicalNumber}
            setNumber={setPhysicalNumber}
            code={physicalCode}
            setCode={setPhysicalCode}
            name={physicalName}
            setName={setPhysicalName}
            proceed={addPhysicalFinish}
          />
        </IonContent>
      </>
    </IonPage>
  );
};

export default CardsAddPhysicalPage;
